"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FileText, Cpu, Search, Clock, Utensils, List, Flame, ArrowRight, Code, CircleCheck } from "lucide-react";
const JSON_SCHEMA_STRING = `
{
  "@context": "https://schema.org/",
  "@type": "LocalBusiness",
  "name": "The Peachtree Post",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Johns Creek",
    "addressRegion": "GA"
  },
  "description": "Premium direct mail marketing for Metro Atlanta businesses.",
  "areaServed": "Johns Creek, GA",
  "serviceType": "Co-op Direct Mail",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock",
    "description": "Exclusive category placement on premium postcards"
  }
}
`.repeat(10);
const schemaOutputs = [{
  icon: Clock,
  label: "Targeted Delivery",
  value: "2,500+ Households"
}, {
  icon: Utensils,
  label: "Category Lock",
  value: "Zero Competitors"
}, {
  icon: List,
  label: "Full Service",
  value: "Design to Delivery"
}, {
  icon: Flame,
  label: "High Impact",
  value: "Premium Cardstock"
}];
export default function TechForTheModernChef() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: diagramRef,
    isVisible: isDiagramVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224598" id="lets-grow-your-business" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes code-rain {
          0% { transform: translateY(-50%); }
          100% { transform: translateY(0%); }
        }
        .animate-code-rain {
          animation: code-rain 40s linear infinite;
        }
      `
    }} />

      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none opacity-15" aria-hidden="true">
        <div className="absolute inset-x-0 top-0 w-full h-[200%] animate-code-rain flex gap-8 px-4">
          {[...Array(3)].map((_, i) => <div key={i} className="flex-1 font-mono text-xs leading-relaxed text-primary whitespace-pre-wrap break-words opacity-70">
              {JSON_SCHEMA_STRING}
            </div>)}
        </div>
      </div>

      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,var(--background)_80%)] pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4">
        
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-primary blur-xl opacity-40 rounded-full" />
            <Badge variant="outline" className="relative border-primary text-primary bg-background/50 backdrop-blur-sm px-4 py-1 text-sm tracking-wider animate-pulse">
              <Code className="w-4 h-4 mr-2" />
              AVAILABILITY STATUS: OPEN FOR Q4
            </Badge>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default mb-6 tracking-tight">
            Claim Your Category Before Your Competitor Does
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">
            The Peachtree Post puts your brand directly into the hands of high-income homeowners in Metro Atlanta. 
            We handle the design, printing, and USPS delivery, ensuring your business stands out 
            on a premium shared mailer with zero local competition.
          </p>
        </div>

        {}
        <div ref={diagramRef} className={`relative max-w-6xl mx-auto transition-all duration-1000 delay-200 transform ${isDiagramVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 items-center bg-card/40 backdrop-blur-md border border-border rounded-3xl p-6 md:p-10 shadow-2xl">
            
            {}
            <div className="flex flex-col items-center text-center space-y-4 bg-background/50 rounded-2xl p-6 border border-border/50">
              <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-2">
                <FileText className="w-8 h-8 text-secondary-foreground" />
              </div>
              <h3 className="text-xl font-semibold font-default">Your Business</h3>
              <p className="text-sm text-muted-foreground font-default">
                Simply provide your logo and offer. We craft a custom, high-conversion ad for you.
              </p>
              <div className="w-full text-left bg-muted/30 rounded-lg p-4 mt-4 font-mono text-xs text-muted-foreground overflow-hidden h-24 relative">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-muted/30 z-10" />
                [AD DESIGN IN PROGRESS]<br />
                Client: Local HVAC Expert<br />
                Offer: $50 Off Seasonal Tune-up<br />
                Target: Johns Creek Households
              </div>
            </div>

            {}
            <div className="flex flex-col items-center justify-center py-8 lg:py-0">
              <div className="hidden lg:block w-px h-16 bg-gradient-to-b from-transparent via-primary/50 to-transparent mb-4" />
              <div className="relative group">
                <div className="absolute inset-0 bg-primary blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 rounded-full" />
                <div className="relative w-20 h-20 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center shadow-lg shadow-primary/10 backdrop-blur-sm">
                  <Cpu className="w-10 h-10 text-primary" />
                </div>
              </div>
              <div className="hidden lg:block w-px h-16 bg-gradient-to-b from-transparent via-primary/50 to-transparent mt-4" />
              <ArrowRight className="w-6 h-6 text-primary/50 lg:hidden mt-4" />
            </div>

            {}
            <div className="flex flex-col space-y-6">
              <div className="text-center lg:text-left mb-2">
                <h3 className="text-xl font-semibold font-default flex items-center justify-center lg:justify-start gap-2">
                  <Search className="w-5 h-5 text-primary" />
                  Precision Mailing
                </h3>
                <p className="text-sm text-muted-foreground mt-2 font-default">
                  Every Door Direct Mail (EDDM) targeting the most affluent zip codes.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {schemaOutputs.map((item, i) => <div key={i} data-index={i} className="bg-background/60 border border-border/50 rounded-xl p-4 flex items-start gap-3 hover:border-primary/30 transition-colors">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <item.icon className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground font-default">{item.label}</div>
                      <div className="text-xs font-mono text-muted-foreground mt-1">{item.value}</div>
                    </div>
                  </div>)}
              </div>

              <div className="bg-primary/10 border border-primary/20 rounded-xl p-3 flex items-center gap-3 mt-2">
                <CircleCheck className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm font-medium text-primary-foreground/90 font-default">
                  Exclusive Category Rights Secured
                </span>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>;
}